import { Extension, mergeAttributes, type AnyExtension } from '@tiptap/core';
import { Plugin, TextSelection } from '@tiptap/pm/state';
import { findWrapping } from '@tiptap/pm/transform';
import { HANDLE_AT_START_RE } from '@leanin/shared';
import HardBreak from '@tiptap/extension-hard-break';
import Link from '@tiptap/extension-link';
import Mention, { type MentionOptions } from '@tiptap/extension-mention';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import Text from '@tiptap/extension-text';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import { Fragment, Slice, type Mark, type Node as ProseMirrorNode } from '@tiptap/pm/model';
import {
  Markdown,
  type MarkdownMarkSpec,
  type MarkdownNodeSpec,
} from 'tiptap-markdown';
import rendererStyles from './RichBodyRenderer.module.css';

// TipTap extension set for the member-facing rich text editors (post
// composer, comment composer, event-form RichTextArea). The document
// serializes to the markdown dialect decided in
// docs/design/decisions/2026-08-04-post-editor-rework.md:
//
//   **bold**  *italic*  <u>underline</u>  - bullet  1. ordered
//   [text](url) links (bare URLs stay bare)  @handle mentions
//   hard break = single \n  paragraph break = blank line
//
// Everything else (headings, quotes, code, strike, tables, images) is
// disabled so the editor cannot produce shapes outside the dialect, and
// literal markdown metacharacters in prose are escaped by the serializer.

type MarkdownSerializerState = Parameters<MarkdownNodeSpec['serialize']>[0];

// tiptap-markdown's default hardBreak spec emits "\\\n" (backslash hard
// break). The dialect stores a bare "\n" — the read side uses remark-breaks
// and the parse side runs markdown-it with `breaks: true`, so a plain
// newline round-trips — and a backslash would pollute search_text and
// body previews. Trailing hard breaks are skipped (same as the default):
// a paragraph's own closing blank line already encodes them ambiguously.
const RichTextHardBreak = HardBreak.extend({
  addStorage() {
    const markdown: MarkdownNodeSpec = {
      serialize(state, node, parent, index) {
        for (let i = index + 1; i < parent.childCount; i++) {
          if (parent.child(i).type !== node.type) {
            state.write('\n');
            return;
          }
        }
      },
      parse: {},
    };
    return { markdown };
  },
});

// Escapes a text run so the markdown parser reads it back as the same
// literal text. Replaces tiptap-markdown's text spec (prosemirror-markdown's
// `esc` + `<`/`>` entities), which leaves three round-trip holes:
//   * `_` beside another `_` counts as a word character, so `__init__`
//     serializes as `\__init_\_` and re-reads as italic;
//   * line-start markers are escaped only at a block's start, but a single
//     `\n` (our hard break) also starts a line — `1. `, `- `, `#`, `===`
//     after a hard break re-read as a list, heading, or setext underline;
//   * an entity-shaped `&amp;` decodes to `&` on the way back in.
// `_` stays bare only between two letters/digits, where CommonMark cannot
// read it as emphasis (snake_case, URL paths). No lookbehind: see the
// Safari note on MENTION_QUERY_RE below.
const ALWAYS_ESCAPED_RE = /[`*\\~[\]]/g;
const WORD_CHAR_RE = /[\p{L}\p{N}]/u;
const ENTITY_AMPERSAND_RE = /&(?=#?[a-z0-9]+;)/gi;
const LINE_START_MARKER_RE = /^(\s*)([-+=#]|\d+(?=[.)](?:\s|$)))/;

function isWordChar(char: string | undefined): boolean {
  return char !== undefined && WORD_CHAR_RE.test(char);
}

function escapeMarkdownText(text: string, atLineStart: boolean): string {
  const escaped = text
    .replace(ALWAYS_ESCAPED_RE, '\\$&')
    .replace(/_/g, (underscore, index: number, source: string) =>
      isWordChar(source[index - 1]) && isWordChar(source[index + 1])
        ? underscore
        : '\\_',
    )
    .replace(ENTITY_AMPERSAND_RE, '\\&')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  if (!atLineStart) return escaped;
  return escaped.replace(LINE_START_MARKER_RE, (_match, indent: string, marker: string) =>
    /^\d/.test(marker) ? `${indent}${marker}\\` : `${indent}\\${marker}`,
  );
}

const RichTextText = Text.extend({
  addStorage() {
    const markdown: MarkdownNodeSpec = {
      serialize(state, node, parent, index) {
        const text = node.text ?? '';
        // A bare-URL link (see RichTextLink) is written verbatim so linkify
        // re-links exactly the URL the member typed.
        if ((state as MarkdownSerializerState & { inAutolink?: boolean }).inAutolink === true) {
          state.text(text, false);
          return;
        }
        // A line starts at the block's first inline node and after every
        // hard break (stored as a bare `\n`).
        const atLineStart = index === 0 || parent.child(index - 1).type.name === 'hardBreak';
        state.text(escapeMarkdownText(text, atLineStart), false);
      },
      parse: {},
    };
    return { markdown };
  },
});

// Bold and italic overlap freely in the editor, but markdown nests them, so a
// mark ending inside another serializes as close-both-then-reopen. When the
// reopening `*` / `**` lands flush against the closing `***`, the runs merge
// (`**Ahave*rrrr****eeeee*`) into one that CommonMark's rule of 3 can't pair:
// markdown-it (the editor's parser) reads the asterisks as literal text, and
// saving an edit then escapes them for good. Such a reopen is written as its
// HTML tag instead — `<strong>` / `<em>` sit in the dialect beside `<u>`, and
// every reader (the editor, RichBodyRenderer, the server's plain-text and
// broadcast converters) takes them. `_` would avoid the merge too but can't
// open or close intraword, which overlapping marks routinely need.
//
// The choice is made in the state's markString, around tiptap-markdown's own:
// a mark written as `*` must stay on its path (it records the delimiter to
// pull it past whitespace), and a tag must stay off it. A reopen followed by
// whitespace keeps `*`, since that path moves it past the space anyway.
const EMPHASIS_TAGS: Readonly<Record<string, string>> = { bold: 'strong', italic: 'em' };

const emphasisGuarded = new WeakSet<MarkdownSerializerState>();

function guardEmphasisJunctions(state: MarkdownSerializerState): void {
  if (emphasisGuarded.has(state)) return;
  emphasisGuarded.add(state);
  const markString = state.markString.bind(state);
  // prosemirror-markdown's typings omit `out`, the serialized text so far.
  const output = (): string => (state as MarkdownSerializerState & { out: string }).out;
  // Per mark name, whether each currently open mark was written as a tag,
  // innermost last (marks close in reverse order of opening).
  const openedAsTag = new Map<string, boolean[]>();
  let lastCloseEnd = -1;
  state.markString = (mark, open, parent, index) => {
    const tag = EMPHASIS_TAGS[mark.type.name];
    if (tag === undefined) return markString(mark, open, parent, index);
    const stack = openedAsTag.get(mark.type.name) ?? [];
    openedAsTag.set(mark.type.name, stack);
    if (open) {
      const next = parent.maybeChild(index);
      const asTag =
        lastCloseEnd === output().length &&
        output().endsWith('*') &&
        !(next?.isText === true && /^\s/.test(next.text ?? ''));
      stack.push(asTag);
      return asTag ? `<${tag}>` : markString(mark, open, parent, index);
    }
    const closing = stack.pop() === true ? `</${tag}>` : markString(mark, open, parent, index);
    lastCloseEnd = output().length + closing.length;
    return closing;
  };
}

const RichTextParagraph = Paragraph.extend({
  addStorage() {
    const markdown: MarkdownNodeSpec = {
      // tiptap-markdown's paragraph, with the emphasis guard installed on the
      // serialization's state first (all inline content lives in paragraphs).
      serialize(state, node) {
        guardEmphasisJunctions(state);
        state.renderInline(node);
        state.closeBlock(node);
      },
      parse: {},
    };
    return { markdown };
  },
});

// Mirrors prosemirror-markdown's isPlainURL: the link's rendered text is
// exactly its href, so the bare URL alone re-parses to the same link.
function isPlainUrl(link: Mark, parent: ProseMirrorNode, index: number): boolean {
  if (typeof link.attrs['href'] !== 'string' || !/^\w+:/.test(link.attrs['href'])) {
    return false;
  }
  const content = parent.child(index);
  if (
    !content.isText ||
    content.text !== link.attrs['href'] ||
    content.marks[content.marks.length - 1] !== link
  ) {
    return false;
  }
  return (
    index === parent.childCount - 1 ||
    !link.isInSet(parent.child(index + 1).marks)
  );
}

// prosemirror-markdown's default link spec serializes a plain URL as an
// `<url>` autolink. The dialect keeps bare URLs bare — `linkify: true` on
// the parse side re-links them — so stored bodies, search_text, and
// notification previews carry the clean URL, matching what members typed.
// `inAutolink` piggybacks on prosemirror-markdown's own escape suppression
// so URL underscores don't get backslash-escaped.
const RichTextLink = Link.extend({
  addStorage() {
    const markdown: MarkdownMarkSpec = {
      serialize: {
        open(state: MarkdownSerializerState & { inAutolink?: boolean }, mark: Mark, parent: ProseMirrorNode, index: number) {
          state.inAutolink = isPlainUrl(mark, parent, index);
          return state.inAutolink ? '' : '[';
        },
        close(state: MarkdownSerializerState & { inAutolink?: boolean }, mark: Mark) {
          const { inAutolink } = state;
          delete state.inAutolink;
          return inAutolink === true
            ? ''
            : `](${String(mark.attrs['href']).replace(/[()"]/g, '\\$&')})`;
        },
        mixable: true,
      },
      parse: {},
    };
    return { markdown };
  },
});

export type MentionNames = ReadonlyMap<string, string>;

type RichTextMentionOptions = MentionOptions & {
  /** handle → display name; `@handle` tokens with an entry hydrate into
   *  chips when a markdown value is loaded, others stay literal text.
   *  Callers pass a ref-stable (mutable) Map they grow as mentions are
   *  picked; the contents are read at parse time. */
  mentionNames: MentionNames | null;
};

// The shared mention grammar's handle part, matched against the text
// starting after an `@`; the walk below checks the lookbehind. `\b`
// backtracking trims trailing dots the same way the renderer/server
// regex does.
const MENTION_AT_START = HANDLE_AT_START_RE;

// The @-mention query under the caret, matched against the text ending at
// the caret. Same boundary rule as the shared mention grammar (an `@`
// preceded by a word character or a dot is not a mention, so emails stay
// literal), then one word plus at most one following word ("first last") so
// a shared first name can be narrowed by a surname — the suggestion closes
// on a second space. The word charset is any non-space rather than the
// handle charset: people type display names ("Benítez"), the server matches
// them case- and accent-insensitively, and only the inserted chip
// serializes to a handle.
//
// The boundary is a captured prefix (`^` or one non-boundary character)
// rather than a lookbehind: a lookbehind literal fails to parse on
// Safari < 16.4, and a module in this graph that fails to parse takes the
// whole bundle down. The prefix is stripped from the reported match.
const MENTION_QUERY_RE = /(^|[^a-zA-Z0-9.])@([^\s@]{0,40}|[^\s@]{1,40} [^\s@]{0,40})$/;

export function mentionQueryMatch(
  textBeforeCaret: string,
): { readonly index: number; readonly text: string; readonly query: string } | null {
  const match = MENTION_QUERY_RE.exec(textBeforeCaret);
  if (match === null) return null;
  const prefixLength = match[1]!.length;
  return {
    index: match.index + prefixLength,
    text: match[0].slice(prefixLength),
    query: match[2]!,
  };
}

type MentionSuggestionConfig = MentionOptions['suggestion'];
type FindSuggestionMatchFn = NonNullable<MentionSuggestionConfig['findSuggestionMatch']>;

/** tiptap adapter around `mentionQueryMatch`: reads the text before the
 *  caret (ProseMirror cuts `nodeBefore` at the caret) and maps the string
 *  match back to document positions. */
export const findMentionSuggestionMatch: FindSuggestionMatchFn = ({ $position }) => {
  const node = $position.nodeBefore;
  const text = node !== null && node.isText ? (node.text ?? '') : '';
  if (text.length === 0) return null;
  const match = mentionQueryMatch(text);
  if (match === null) return null;
  const textFrom = $position.pos - text.length;
  return {
    range: { from: textFrom + match.index, to: $position.pos },
    query: match.query,
    text: match.text,
  };
};

function mentionChipSpan(
  doc: Document,
  handle: string,
  name: string,
): HTMLSpanElement {
  const span = doc.createElement('span');
  span.setAttribute('data-type', 'mention');
  span.setAttribute('data-id', handle);
  span.setAttribute('data-label', name);
  return span;
}

function chipifyTextNode(node: Text, names: MentionNames): void {
  const text = node.textContent ?? '';
  const doc = node.ownerDocument;
  const parts: Array<string | HTMLSpanElement> = [];
  let plainFrom = 0;
  let i = 0;
  while (i < text.length) {
    if (
      text[i] === '@' &&
      (i === 0 || !/[a-zA-Z0-9.]/.test(text[i - 1]!))
    ) {
      const match = MENTION_AT_START.exec(text.slice(i + 1));
      const name = match !== null ? names.get(match[0]) : undefined;
      if (match !== null && name !== undefined) {
        parts.push(text.slice(plainFrom, i));
        parts.push(mentionChipSpan(doc, match[0], name));
        i += 1 + match[0].length;
        plainFrom = i;
        continue;
      }
    }
    i++;
  }
  if (plainFrom === 0) return;
  parts.push(text.slice(plainFrom));
  const parent = node.parentNode;
  if (parent === null) return;
  for (const part of parts) {
    if (typeof part === 'string') {
      if (part.length > 0) parent.insertBefore(doc.createTextNode(part), node);
    } else {
      parent.insertBefore(part, node);
    }
  }
  parent.removeChild(node);
}

function chipifyMentions(root: HTMLElement, names: MentionNames): void {
  for (const child of Array.from(root.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      chipifyTextNode(child as Text, names);
      continue;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue;
    const el = child as HTMLElement;
    if (el.tagName === 'A' || el.getAttribute('data-type') === 'mention') {
      continue;
    }
    chipifyMentions(el, names);
  }
}

const RichTextMention = Mention.extend<RichTextMentionOptions>({
  addOptions() {
    return {
      ...(this.parent?.() as MentionOptions),
      mentionNames: null,
      renderText({ node }) {
        return `@${node.attrs['id']}`;
      },
      renderHTML({ options, node }) {
        return [
          'span',
          mergeAttributes(options.HTMLAttributes, {
            class: rendererStyles['mention'],
          }),
          String(node.attrs['label'] ?? `@${node.attrs['id']}`),
        ];
      },
    };
  },
  addStorage() {
    const markdown: MarkdownNodeSpec<RichTextMentionOptions> = {
      serialize(state, node) {
        state.write(`@${node.attrs['id']}`);
      },
      parse: {
        updateDOM(element) {
          const names = this.options.mentionNames;
          if (names !== null && names.size > 0) {
            chipifyMentions(element, names);
          }
        },
      },
    };
    return { markdown };
  },
});

export type RichTextExtensionsOptions = {
  readonly placeholder?: string;
  readonly mentionNames?: MentionNames;
  /** Suggestion-popup wiring (trigger char, command, render lifecycle).
   *  Provided by RichTextEditor; omitted in headless/serialization use. */
  readonly mentionSuggestion?: MentionOptions['suggestion'];
};

// The "- " / "1. " list shortcuts are input rules anchored to the start of a
// textblock, but a new line typed with Shift+Enter is a hard break inside the
// same paragraph, so the marker there stayed literal text. This catches the
// space that completes a marker right after a hard break and turns that line
// into a list: the break becomes a paragraph split, the marker is dropped, and
// the new paragraph is wrapped — what the rules do for a marker typed at a
// block's start.
const LINE_LIST_MARKER_RE = /^\s*(?:[-+*]|(\d+)\.)$/;

const ListShortcutAfterLineBreak = Extension.create({
  name: 'listShortcutAfterLineBreak',
  addProseMirrorPlugins() {
    const { schema } = this.editor;
    return [
      new Plugin({
        props: {
          handleTextInput(view, from, to, text) {
            if (text !== ' ' || from !== to) return false;
            const $from = view.state.doc.resolve(from);
            const paragraph = $from.parent;
            if (paragraph.type.name !== 'paragraph') return false;
            if ($from.node(-1).type.name === 'listItem') return false;

            let breakOffset = -1;
            paragraph.forEach((child, offset) => {
              if (child.type.name === 'hardBreak' && offset < $from.parentOffset) {
                breakOffset = offset;
              }
            });
            if (breakOffset < 0) return false;
            const marker = LINE_LIST_MARKER_RE.exec(
              paragraph.textBetween(breakOffset + 1, $from.parentOffset, undefined, '￼'),
            );
            if (marker === null) return false;

            const listType =
              marker[1] === undefined ? schema.nodes['bulletList'] : schema.nodes['orderedList'];
            if (listType === undefined) return false;
            const start = Number(marker[1] ?? '1');
            const attrs = marker[1] !== undefined && start !== 1 ? { start } : null;

            const breakPos = $from.start() + breakOffset;
            const tr = view.state.tr.delete(breakPos, from).split(breakPos);
            tr.setSelection(TextSelection.create(tr.doc, breakPos + 2));
            const range = tr.selection.$from.blockRange();
            const wrapping = range === null ? null : findWrapping(range, listType, attrs);
            if (range === null || wrapping === null) return false;
            view.dispatch(tr.wrap(range, wrapping).scrollIntoView());
            return true;
          },
        },
      }),
    ];
  },
});

// A whole-line copy (select all, triple-click) puts closed paragraphs on the
// clipboard, and ProseMirror inserts a closed block as a block of its own, so
// pasting "testing testing" mid-line split the line in two. When everything
// pasted is plain paragraphs, the slice is opened at both ends: the first
// paragraph joins the line at the caret and the last joins what follows it,
// as a plain-text paste would. Anything else (a list, say) pastes as copied.
// ProseMirror runs this hook for drops too; a paragraph dragged within the
// editor is being moved as a block, so it is left closed.
const PasteParagraphsInline = Extension.create({
  name: 'pasteParagraphsInline',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          transformPasted(slice, view) {
            if (view.dragging !== null) return slice;
            if (slice.openStart > 0 && slice.openEnd > 0) return slice;
            let paragraphsOnly = slice.content.childCount > 0;
            slice.content.forEach((node) => {
              if (node.type.name !== 'paragraph') paragraphsOnly = false;
            });
            return paragraphsOnly ? new Slice(slice.content, 1, 1) : slice;
          },
        },
      }),
    ];
  },
});

export function richTextExtensions(
  options: RichTextExtensionsOptions = {},
): AnyExtension[] {
  return [
    StarterKit.configure({
      paragraph: false,
      heading: false,
      code: false,
      codeBlock: false,
      blockquote: false,
      horizontalRule: false,
      strike: false,
      hardBreak: false,
      text: false,
    }),
    RichTextParagraph,
    RichTextText,
    RichTextHardBreak,
    ListShortcutAfterLineBreak,
    PasteParagraphsInline,
    Underline,
    RichTextLink.configure({
      openOnClick: false,
      autolink: true,
      defaultProtocol: 'https',
      isAllowedUri: (url, ctx) =>
        ctx.defaultValidate(url) && /^https?:\/\//i.test(url),
    }),
    RichTextMention.configure({
      mentionNames: options.mentionNames ?? null,
      ...(options.mentionSuggestion !== undefined
        ? { suggestion: options.mentionSuggestion }
        : {}),
    }),
    Placeholder.configure({ placeholder: options.placeholder ?? '' }),
    Markdown.configure({
      html: true,
      breaks: true,
      linkify: true,
      tightLists: true,
      bulletListMarker: '-',
    }),
  ];
}

export function editorMarkdown(editor: {
  storage: Record<string, unknown>;
  state: { doc: ProseMirrorNode };
}): string {
  const { serializer } = editor.storage['markdown'] as {
    serializer: { serialize: (doc: ProseMirrorNode) => string };
  };
  return serializer.serialize(withoutBlankListItems(editor.state.doc));
}

// A list item with nothing visible in it (the one a member has just opened,
// or one holding only line breaks) carries no content, so the stored body
// drops it — and a list left with no items goes too — rather than rendering
// as a bare bullet or number. The live document keeps it; only the
// serialized dialect is trimmed.
function withoutBlankListItems(node: ProseMirrorNode): ProseMirrorNode {
  if (node.isTextblock || node.isLeaf) return node;
  const children: ProseMirrorNode[] = [];
  node.forEach((child) => {
    if (child.type.name === 'listItem' && isBlank(child)) return;
    const trimmed = withoutBlankListItems(child);
    if (LIST_NODE_NAMES.has(trimmed.type.name) && trimmed.childCount === 0) return;
    children.push(trimmed);
  });
  return node.copy(Fragment.from(children));
}

const LIST_NODE_NAMES = new Set(['bulletList', 'orderedList']);

function isBlank(node: ProseMirrorNode): boolean {
  let blank = true;
  node.descendants((descendant) => {
    if (!blank) return false;
    if (descendant.isText) blank = (descendant.text ?? '').trim() === '';
    else if (descendant.isInline && descendant.type.name !== 'hardBreak') blank = false;
    return blank;
  });
  return blank;
}
